import React, { useEffect, useState } from "react";
import s from "./PopoverItems.module.scss";

import { useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../../../../libs/api/userSession";
import ForwardIcon from "../../../../../../../../../global components/Icons/ForwardIcon";
import EyeOpen from "../../../../../../../../../global components/Icons/EyeOpen/EyeOpen";
import { SuccessDataType } from "../../../../../../../../../libs/api/types";
import { generateResult } from "../../../../../../../../../global components/ResultModal/ResultModal";
import ReusableModal from "../../../../../../../../../global components/Modal/Modal";
import { Button, Input } from "antd";
import { CloseIcon } from "../../../../../../../../../global components/Icons/CloseIcon";
import { BaselineCheckCircle } from "../../../../../../../../../global components/Icons/BaseLineCheckCircle";
import ReactEmailEditor from "../../ReactEmailEditor";
import clsx from "clsx";
import { ApplicationMessageDetails } from "../../../../../../../../../libs/Utils/Types/User";
import useSWR from "swr";
import { Oval } from "react-loader-spinner";

interface PopOverItemsProps {
  keyValue: string; // Add a prop for the key value
  mutate: () => void;
  type: string;
  msgType: "sent" | "scheduled" | "draft";
}
function PopOverItems({ keyValue, mutate, type, msgType }: PopOverItemsProps) {
  const [contentJSONDraft, setContentJSONDraft] = useState<any>();
  const [contentHTMLDraft, setContentHTMLDraft] = useState<any>();
  const [openEditor, setOpenEditor] = useState(false);
  const [msgTitle, setMsgTitle] = useState("");
  const [audience, setAudience] = useState("");
  const navigate = useNavigate();

  const token = UseAuthToken();
  const { data: emailDetails, isLoading } = useSWR<
    { data: ApplicationMessageDetails },
    any,
    any
  >(token ? [`api/admin/app-messages/${keyValue}`, token] : null, swrFetcher, {
    shouldRetryOnError: false, // Disable retrying on errors
  });
  const { trigger: submitMessage, isMutating } = useSWRMutation(
    `api/admin/app-messages/${keyValue}`,
    swrMutationFetcher,
  );
  const { trigger: submitDraftMessage, isMutating: isSubmitting } =
    useSWRMutation(`api/admin/app-messages/${keyValue}`, swrMutationFetcher);
  const toggleEditorModal = () => setOpenEditor((p) => !p);

  const handleEdit = (_html: string, json: any) => {
    setContentJSONDraft(json);
    setContentHTMLDraft(_html);
  };

  const handleSave = async () => {
    if (!msgTitle)
      return generateResult("error", "Enter a message title before sending");
    if (!contentJSONDraft)
      return generateResult("error", "Create a message before sending");

    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          method: "PATCH",
          body: JSON.stringify({
            title: msgTitle,
            body: contentHTMLDraft,
            audience: audience,
            meta: contentJSONDraft,
            sentAt: new Date().toISOString(),
          }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await submitMessage(fetcherArg);
      generateResult("success", result.message);
      toggleEditorModal();
      mutate();
    } catch (error) {
      const e = parseClientError(error);
      generateResult("error", e.message || "something went wrong");
    }
  };
  const handleDraft = async () => {
    if (!msgTitle)
      return generateResult("error", "Enter a message title before sending");
    if (!contentJSONDraft)
      return generateResult("error", "Create a message before sending");

    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          method: "Patch",
          body: JSON.stringify({
            title: msgTitle,
            body: contentHTMLDraft,
            audience: audience,
            meta: contentJSONDraft,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await submitDraftMessage(fetcherArg);
      generateResult("success", result.message);
      toggleEditorModal();
      mutate();
    } catch (error) {
      const e = parseClientError(error);
      generateResult("error", e.message || "something went wrong");
    }
  };
  useEffect(() => {
    if (emailDetails) {
      setAudience(emailDetails.data.audience);
      setContentJSONDraft(emailDetails.data.meta);
      setMsgTitle(emailDetails.data.title);
      setContentHTMLDraft(emailDetails.data.body);
    }
  }, [emailDetails]);
  return (
    <>
      <div className={s.wrapper}>
        {
          <span
            className={s.PopOverItem}
            onClick={() =>
              navigate(`/dashboard/users/send-message/details/${keyValue}`)
            }
          >
            <EyeOpen fill="#240806" />
            <h5>View details</h5>
          </span>
        }
        {
          <span
            className={s.PopOverItem}
            onClick={() => !isLoading && toggleEditorModal()}
          >
            {isLoading && <Oval width={15} height={15} />}
            <ForwardIcon height="18" width="18" color="#240806" />
            <h5>{msgType === "draft" ? "Edit message" : "Forward message"}</h5>
          </span>
        }
      </div>
      <ReusableModal
        isModalOpen={openEditor}
        handleModal={toggleEditorModal}
        footer={null}
        centered
        width={"90vw"}
        className={s.editorModal}
        closable={false}
        maskClosable={false}
        contentClassName={s.editorModal}
        title={
          <div className={s.header}>
            <Input
              value={msgTitle}
              count={{ show: true, max: 80 }}
              className={clsx(s.input, { [s.inputError]: !msgTitle })}
              maxLength={80}
              placeholder="This is the title or subject of email notifications."
              onChange={(e) => setMsgTitle(e.target.value)}
            />

            <div className={s.btnGroup}>
              <button onClick={toggleEditorModal} className={s.closeBtn}>
                <CloseIcon />
                Cancel
              </button>
              <Button
                loading={isMutating}
                className={s.saveBtn}
                onClick={handleSave}
              >
                <BaselineCheckCircle />
                Send message
              </Button>
              {msgType !== "sent" && (
                <Button
                  className={s.saveBtn}
                  loading={isSubmitting}
                  onClick={handleDraft}
                >
                  <BaselineCheckCircle />
                  Save as draft
                </Button>
              )}
            </div>
          </div>
        }
        classNames={{
          body: s.modalBody,
          wrapper: s.modalWrapper,
          content: s.modalContent,
          footer: s.modalFooter,
          header: s.modalHeader,
          mask: s.modalMask,
        }}
      >
        <ReactEmailEditor
          handleEditorHTMLValue={handleEdit}
          jsonFormat={contentJSONDraft}
        />
      </ReusableModal>
    </>
  );
}

export default PopOverItems;
